'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type CarouselDirection = 'horizontal' | 'vertical';
export type CarouselTransition = 'slide' | 'fade' | 'none';
export type CarouselSize = 'sm' | 'md' | 'lg' | 'full';

export interface CarouselContextValue {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  itemCount: number;
  direction: CarouselDirection;
  transition: CarouselTransition;
  loop: boolean;
  next: () => void;
  previous: () => void;
  goTo: (index: number) => void;
}

const CarouselContext = React.createContext<CarouselContextValue | null>(null);

function useCarouselContext() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error('Carousel components must be used within a Carousel');
  }
  return context;
}

// ============================================================================
// Carousel
// ============================================================================

export interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultIndex?: number;
  activeIndex?: number;
  onIndexChange?: (index: number) => void;
  direction?: CarouselDirection;
  transition?: CarouselTransition;
  loop?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  pauseOnHover?: boolean;
  children: React.ReactNode;
}

export function Carousel({
  defaultIndex = 0,
  activeIndex: controlledIndex,
  onIndexChange,
  direction = 'horizontal',
  transition = 'slide',
  loop = false,
  autoPlay = false,
  autoPlayInterval = 3000,
  pauseOnHover = true,
  children,
  className,
  ...props
}: CarouselProps) {
  const [uncontrolledIndex, setUncontrolledIndex] = React.useState(defaultIndex);
  const [isPaused, setIsPaused] = React.useState(false);

  const isControlled = controlledIndex !== undefined;
  const activeIndex = isControlled ? controlledIndex : uncontrolledIndex;

  // Count items - look inside CarouselContent
  const itemCount = React.useMemo(() => {
    let count = 0;
    const countItems = (children: React.ReactNode) => {
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === CarouselItem) {
            count++;
          } else if (child.props?.children) {
            countItems(child.props.children);
          }
        }
      });
    };
    countItems(children);
    return count;
  }, [children]);

  const setActiveIndex = React.useCallback(
    (index: number) => {
      if (!isControlled) {
        setUncontrolledIndex(index);
      }
      onIndexChange?.(index);
    },
    [isControlled, onIndexChange]
  );

  const next = React.useCallback(() => {
    let newIndex = activeIndex + 1;
    if (newIndex >= itemCount) {
      newIndex = loop ? 0 : itemCount - 1;
    }
    setActiveIndex(newIndex);
  }, [activeIndex, itemCount, loop, setActiveIndex]);

  const previous = React.useCallback(() => {
    let newIndex = activeIndex - 1;
    if (newIndex < 0) {
      newIndex = loop ? itemCount - 1 : 0;
    }
    setActiveIndex(newIndex);
  }, [activeIndex, itemCount, loop, setActiveIndex]);

  const goTo = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < itemCount) {
        setActiveIndex(index);
      }
    },
    [itemCount, setActiveIndex]
  );

  // Auto play
  React.useEffect(() => {
    if (!autoPlay || isPaused || itemCount <= 1) return;

    const timer = setInterval(() => {
      next();
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [autoPlay, autoPlayInterval, isPaused, itemCount, next]);

  const contextValue: CarouselContextValue = {
    activeIndex,
    setActiveIndex,
    itemCount,
    direction,
    transition,
    loop,
    next,
    previous,
    goTo,
  };

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        className={clsx('carousel relative overflow-hidden', className)}
        onMouseEnter={() => pauseOnHover && setIsPaused(true)}
        onMouseLeave={() => pauseOnHover && setIsPaused(false)}
        role="region"
        aria-roledescription="carousel"
        aria-label="Image carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

// ============================================================================
// CarouselContent
// ============================================================================

export interface CarouselContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function CarouselContent({
  children,
  className,
  ...props
}: CarouselContentProps) {
  const { activeIndex, direction, transition } = useCarouselContext();
  const isHorizontal = direction === 'horizontal';

  const transformStyle =
    transition === 'slide'
      ? {
          transform: isHorizontal
            ? `translateX(-${activeIndex * 100}%)`
            : `translateY(-${activeIndex * 100}%)`,
        }
      : {};

  return (
    <div
      className={clsx(
        'carousel-content',
        isHorizontal ? 'flex' : 'flex flex-col',
        transition === 'slide' && 'transition-transform duration-300 ease-in-out',
        className
      )}
      style={transformStyle}
      aria-live="polite"
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CarouselItem
// ============================================================================

export interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  index?: number;
  children: React.ReactNode;
}

export function CarouselItem({
  index,
  children,
  className,
  ...props
}: CarouselItemProps) {
  const { activeIndex, transition } = useCarouselContext();
  const isActive = index === activeIndex;

  return (
    <div
      className={clsx(
        'carousel-item flex-shrink-0 w-full',
        transition === 'fade' && 'absolute inset-0 transition-opacity duration-300',
        transition === 'fade' && (isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'),
        className
      )}
      role="group"
      aria-roledescription="slide"
      aria-label={`Slide ${(index ?? 0) + 1}`}
      aria-hidden={transition === 'fade' && !isActive}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CarouselPrevious
// ============================================================================

export interface CarouselPreviousProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
}

export function CarouselPrevious({
  icon,
  className,
  ...props
}: CarouselPreviousProps) {
  const { previous, activeIndex, loop } = useCarouselContext();
  const isDisabled = !loop && activeIndex === 0;

  return (
    <button
      type="button"
      className={clsx(
        'carousel-prev absolute left-2 top-1/2 -translate-y-1/2 z-20',
        'w-10 h-10 rounded-full bg-white/80 dark:bg-gray-800/80 shadow-md',
        'flex items-center justify-center',
        'hover:bg-white dark:hover:bg-gray-800 transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      onClick={previous}
      disabled={isDisabled}
      aria-label="Previous slide"
      {...props}
    >
      {icon || (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      )}
    </button>
  );
}

// ============================================================================
// CarouselNext
// ============================================================================

export interface CarouselNextProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
}

export function CarouselNext({
  icon,
  className,
  ...props
}: CarouselNextProps) {
  const { next, activeIndex, itemCount, loop } = useCarouselContext();
  const isDisabled = !loop && activeIndex === itemCount - 1;

  return (
    <button
      type="button"
      className={clsx(
        'carousel-next absolute right-2 top-1/2 -translate-y-1/2 z-20',
        'w-10 h-10 rounded-full bg-white/80 dark:bg-gray-800/80 shadow-md',
        'flex items-center justify-center',
        'hover:bg-white dark:hover:bg-gray-800 transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      onClick={next}
      disabled={isDisabled}
      aria-label="Next slide"
      {...props}
    >
      {icon || (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      )}
    </button>
  );
}

// ============================================================================
// CarouselIndicators
// ============================================================================

export interface CarouselIndicatorsProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'dots' | 'lines' | 'numbers';
}

export function CarouselIndicators({
  variant = 'dots',
  className,
  ...props
}: CarouselIndicatorsProps) {
  const { activeIndex, itemCount, goTo } = useCarouselContext();

  return (
    <div
      className={clsx(
        'carousel-indicators flex items-center justify-center gap-2 mt-4',
        className
      )}
      role="tablist"
      aria-label="Carousel navigation"
      {...props}
    >
      {Array.from({ length: itemCount }).map((_, index) => {
        const isActive = index === activeIndex;

        if (variant === 'numbers') {
          return (
            <button
              key={index}
              type="button"
              className={clsx(
                'w-6 h-6 rounded-full text-xs font-medium transition-colors',
                isActive
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-400'
              )}
              onClick={() => goTo(index)}
              role="tab"
              aria-selected={isActive}
              aria-label={`Go to slide ${index + 1}`}
            >
              {index + 1}
            </button>
          );
        }

        if (variant === 'lines') {
          return (
            <button
              key={index}
              type="button"
              className={clsx(
                'h-1 rounded-full transition-all',
                isActive ? 'w-8 bg-blue-500' : 'w-4 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600'
              )}
              onClick={() => goTo(index)}
              role="tab"
              aria-selected={isActive}
              aria-label={`Go to slide ${index + 1}`}
            />
          );
        }

        return (
          <button
            key={index}
            type="button"
            className={clsx(
              'w-2.5 h-2.5 rounded-full transition-colors',
              isActive
                ? 'bg-blue-500'
                : 'bg-gray-300 hover:bg-gray-400 dark:bg-gray-600'
            )}
            onClick={() => goTo(index)}
            role="tab"
            aria-selected={isActive}
            aria-label={`Go to slide ${index + 1}`}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// Slider - Value range slider
// ============================================================================

export interface SliderProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  showValue?: boolean;
  showLabels?: boolean;
  formatValue?: (value: number) => string;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  marks?: { value: number; label?: string }[];
}

export function Slider({
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  showValue = false,
  showLabels = false,
  formatValue = (v) => String(v),
  size = 'md',
  color,
  marks,
  className,
  disabled,
  ...props
}: SliderProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const percentage = ((value - min) / (max - min)) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    if (!isControlled) {
      setUncontrolledValue(newValue);
    }
    onChange?.(newValue);
  };

  const trackHeights = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  const thumbSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className={clsx('slider w-full', className)}>
      {showLabels && (
        <div className="flex justify-between text-xs text-gray-500 mb-1 dark:text-gray-400">
          <span>{formatValue(min)}</span>
          <span>{formatValue(max)}</span>
        </div>
      )}
      <div className="relative">
        <div
          className={clsx(
            'slider-track w-full rounded-full bg-gray-200 dark:bg-gray-700',
            trackHeights[size]
          )}
        >
          <div
            className={clsx('slider-fill h-full rounded-full', !color && 'bg-blue-500')}
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
            }}
          />
        </div>
        <input
          type="range"
          className={clsx(
            'slider-input absolute inset-0 w-full opacity-0 cursor-pointer',
            disabled && 'cursor-not-allowed'
          )}
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={handleChange}
          disabled={disabled}
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          {...props}
        />
        <div
          className={clsx(
            'slider-thumb absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full',
            'bg-white border-2 border-blue-500 shadow dark:bg-gray-800',
            thumbSizes[size],
            disabled && 'opacity-50'
          )}
          style={{
            left: `${percentage}%`,
            borderColor: color,
          }}
        />
        {marks && marks.length > 0 && (
          <div className="slider-marks absolute w-full top-full mt-2">
            {marks.map((mark) => {
              const markPercentage = ((mark.value - min) / (max - min)) * 100;
              return (
                <div
                  key={mark.value}
                  className="absolute -translate-x-1/2"
                  style={{ left: `${markPercentage}%` }}
                >
                  <div className="w-0.5 h-2 bg-gray-300 dark:bg-gray-600 mx-auto" />
                  {mark.label && (
                    <span className="text-xs text-gray-500 mt-1 block text-center dark:text-gray-400">
                      {mark.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showValue && (
        <div className="text-center text-sm font-medium mt-2">
          {formatValue(value)}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// RangeSlider - Dual-handle range selection
// ============================================================================

export interface RangeSliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  value?: [number, number];
  defaultValue?: [number, number];
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: [number, number]) => void;
  showValues?: boolean;
  formatValue?: (value: number) => string;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  disabled?: boolean;
}

export function RangeSlider({
  value: controlledValue,
  defaultValue = [25, 75],
  min = 0,
  max = 100,
  step = 1,
  onChange,
  showValues = false,
  formatValue = (v) => String(v),
  size = 'md',
  color,
  disabled,
  className,
  ...props
}: RangeSliderProps) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const [minVal, maxVal] = value;
  const minPercentage = ((minVal - min) / (max - min)) * 100;
  const maxPercentage = ((maxVal - min) / (max - min)) * 100;

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), maxVal - step);
    const newValue: [number, number] = [newMin, maxVal];
    if (!isControlled) {
      setUncontrolledValue(newValue);
    }
    onChange?.(newValue);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), minVal + step);
    const newValue: [number, number] = [minVal, newMax];
    if (!isControlled) {
      setUncontrolledValue(newValue);
    }
    onChange?.(newValue);
  };

  const trackHeights = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  const thumbSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className={clsx('range-slider w-full', className)} {...props}>
      <div className="relative">
        <div
          className={clsx(
            'slider-track w-full rounded-full bg-gray-200 dark:bg-gray-700',
            trackHeights[size]
          )}
        >
          <div
            className={clsx('slider-range h-full absolute rounded-full', !color && 'bg-blue-500')}
            style={{
              left: `${minPercentage}%`,
              width: `${maxPercentage - minPercentage}%`,
              backgroundColor: color,
            }}
          />
        </div>
        <input
          type="range"
          className="absolute inset-0 w-full opacity-0 cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto"
          value={minVal}
          min={min}
          max={max}
          step={step}
          onChange={handleMinChange}
          disabled={disabled}
          aria-label="Minimum value"
        />
        <input
          type="range"
          className="absolute inset-0 w-full opacity-0 cursor-pointer pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto"
          value={maxVal}
          min={min}
          max={max}
          step={step}
          onChange={handleMaxChange}
          disabled={disabled}
          aria-label="Maximum value"
        />
        <div
          className={clsx(
            'slider-thumb absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full',
            'bg-white border-2 border-blue-500 shadow z-10 dark:bg-gray-800',
            thumbSizes[size],
            disabled && 'opacity-50'
          )}
          style={{
            left: `${minPercentage}%`,
            borderColor: color,
          }}
        />
        <div
          className={clsx(
            'slider-thumb absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full',
            'bg-white border-2 border-blue-500 shadow z-10 dark:bg-gray-800',
            thumbSizes[size],
            disabled && 'opacity-50'
          )}
          style={{
            left: `${maxPercentage}%`,
            borderColor: color,
          }}
        />
      </div>
      {showValues && (
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-2">
          <span>{formatValue(minVal)}</span>
          <span>{formatValue(maxVal)}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ImageGallery - Image gallery with thumbnails
// ============================================================================

export interface GalleryImage {
  src: string;
  alt: string;
  thumbnail?: string;
}

export interface ImageGalleryProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  images: GalleryImage[];
  defaultIndex?: number;
  showThumbnails?: boolean;
  showNavigation?: boolean;
  aspectRatio?: 'square' | '4:3' | '16:9' | 'auto';
  onChange?: (index: number) => void;
}

export function ImageGallery({
  images,
  defaultIndex = 0,
  showThumbnails = true,
  showNavigation = true,
  aspectRatio = '16:9',
  onChange,
  className,
  ...props
}: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = React.useState(defaultIndex);

  const handleIndexChange = (index: number) => {
    setActiveIndex(index);
    onChange?.(index);
  };

  const aspectRatioClasses = {
    square: 'aspect-square',
    '4:3': 'aspect-[4/3]',
    '16:9': 'aspect-video',
    auto: '',
  };

  if (images.length === 0) {
    return (
      <div className={clsx('image-gallery text-center py-8 text-gray-500 dark:text-gray-400', className)} {...props}>
        No images
      </div>
    );
  }

  return (
    <div className={clsx('image-gallery', className)} {...props}>
      <Carousel
        activeIndex={activeIndex}
        onIndexChange={handleIndexChange}
        loop
      >
        <div className={clsx('relative', aspectRatioClasses[aspectRatio])}>
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index} index={index}>
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {showNavigation && images.length > 1 && (
            <>
              <CarouselPrevious />
              <CarouselNext />
            </>
          )}
        </div>
        {showThumbnails && images.length > 1 && (
          <div className="flex gap-2 mt-2 overflow-x-auto py-1">
            {images.map((image, index) => (
              <button
                key={index}
                type="button"
                className={clsx(
                  'flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-colors',
                  activeIndex === index
                    ? 'border-blue-500'
                    : 'border-transparent hover:border-gray-300'
                )}
                onClick={() => handleIndexChange(index)}
                aria-label={`View image ${index + 1}`}
              >
                <img
                  src={image.thumbnail || image.src}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </Carousel>
    </div>
  );
}

// ============================================================================
// Slideshow - Auto-playing slideshow
// ============================================================================

export interface SlideshowSlide {
  id: string;
  image: string;
  title?: string;
  subtitle?: string;
  link?: string;
}

export interface SlideshowProps extends React.HTMLAttributes<HTMLDivElement> {
  slides: SlideshowSlide[];
  autoPlayInterval?: number;
  showIndicators?: boolean;
  showNavigation?: boolean;
  overlay?: boolean;
  height?: string;
  onSlideClick?: (slide: SlideshowSlide) => void;
}

export function Slideshow({
  slides,
  autoPlayInterval = 5000,
  showIndicators = true,
  showNavigation = true,
  overlay = true,
  height = '400px',
  onSlideClick,
  className,
  ...props
}: SlideshowProps) {
  if (slides.length === 0) {
    return (
      <div className={clsx('slideshow text-center py-8 text-gray-500 dark:text-gray-400', className)} {...props}>
        No slides
      </div>
    );
  }

  return (
    <Carousel
      autoPlay
      autoPlayInterval={autoPlayInterval}
      loop
      className={clsx('slideshow', className)}
      {...props}
    >
      <div className="relative" style={{ height }}>
        <CarouselContent>
          {slides.map((slide, index) => (
            <CarouselItem key={slide.id} index={index}>
              <div
                className={clsx(
                  'relative w-full h-full',
                  onSlideClick && 'cursor-pointer'
                )}
                onClick={() => onSlideClick?.(slide)}
              >
                <img
                  src={slide.image}
                  alt={slide.title || `Slide ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {overlay && (slide.title || slide.subtitle) && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
                    {slide.title && (
                      <h3 className="text-white text-2xl font-bold">
                        {slide.title}
                      </h3>
                    )}
                    {slide.subtitle && (
                      <p className="text-white/80 mt-1">{slide.subtitle}</p>
                    )}
                  </div>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {showNavigation && slides.length > 1 && (
          <>
            <CarouselPrevious />
            <CarouselNext />
          </>
        )}
      </div>
      {showIndicators && slides.length > 1 && (
        <CarouselIndicators variant="lines" />
      )}
    </Carousel>
  );
}

// ============================================================================
// CardCarousel - Card-based carousel
// ============================================================================

export interface CardCarouselItem {
  id: string;
  title: string;
  description?: string;
  image?: string;
  badge?: string;
  footer?: React.ReactNode;
}

export interface CardCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  items: CardCarouselItem[];
  showNavigation?: boolean;
  showIndicators?: boolean;
  onCardClick?: (item: CardCarouselItem) => void;
}

export function CardCarousel({
  items,
  showNavigation = true,
  showIndicators = true,
  onCardClick,
  className,
  ...props
}: CardCarouselProps) {
  if (items.length === 0) {
    return (
      <div className={clsx('card-carousel text-center py-8 text-gray-500 dark:text-gray-400', className)} {...props}>
        No items
      </div>
    );
  }

  return (
    <Carousel loop className={clsx('card-carousel', className)} {...props}>
      <CarouselContent>
        {items.map((item, index) => (
          <CarouselItem key={item.id} index={index}>
            <div
              className={clsx(
                'card bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mx-4',
                onCardClick && 'cursor-pointer hover:shadow-lg transition-shadow'
              )}
              onClick={() => onCardClick?.(item)}
            >
              {item.image && (
                <div className="relative">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                  {item.badge && (
                    <span className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
              <div className="p-4">
                <h4 className="font-semibold text-lg">{item.title}</h4>
                {item.description && (
                  <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
                    {item.description}
                  </p>
                )}
                {item.footer && <div className="mt-3">{item.footer}</div>}
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      {showNavigation && items.length > 1 && (
        <>
          <CarouselPrevious />
          <CarouselNext />
        </>
      )}
      {showIndicators && items.length > 1 && <CarouselIndicators />}
    </Carousel>
  );
}

// ============================================================================
// TestimonialCarousel - Testimonial rotator
// ============================================================================

export interface Testimonial {
  id: string;
  content: string;
  author: {
    name: string;
    title?: string;
    avatar?: string;
    company?: string;
  };
  rating?: number;
}

export interface TestimonialCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  testimonials: Testimonial[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showRating?: boolean;
}

export function TestimonialCarousel({
  testimonials,
  autoPlay = true,
  autoPlayInterval = 5000,
  showRating = true,
  className,
  ...props
}: TestimonialCarouselProps) {
  if (testimonials.length === 0) {
    return (
      <div className={clsx('testimonial-carousel text-center py-8 text-gray-500 dark:text-gray-400', className)} {...props}>
        No testimonials
      </div>
    );
  }

  return (
    <Carousel
      autoPlay={autoPlay}
      autoPlayInterval={autoPlayInterval}
      loop
      className={clsx('testimonial-carousel', className)}
      {...props}
    >
      <CarouselContent>
        {testimonials.map((testimonial, index) => (
          <CarouselItem key={testimonial.id} index={index}>
            <div className="testimonial text-center px-8 py-6">
              {showRating && testimonial.rating && (
                <div className="flex justify-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span
                      key={i}
                      className={clsx(
                        'text-xl',
                        i < testimonial.rating!
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      )}
                    >
                      ★
                    </span>
                  ))}
                </div>
              )}
              <blockquote className="text-lg italic text-gray-700 dark:text-gray-300">
                &ldquo;{testimonial.content}&rdquo;
              </blockquote>
              <div className="flex items-center justify-center gap-3 mt-4">
                {testimonial.author.avatar && (
                  <img
                    src={testimonial.author.avatar}
                    alt={testimonial.author.name}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div className="text-left">
                  <div className="font-semibold">{testimonial.author.name}</div>
                  {(testimonial.author.title || testimonial.author.company) && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {testimonial.author.title}
                      {testimonial.author.title && testimonial.author.company && ', '}
                      {testimonial.author.company}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      {testimonials.length > 1 && <CarouselIndicators />}
    </Carousel>
  );
}

// ============================================================================
// useCarousel Hook
// ============================================================================

export interface UseCarouselOptions {
  itemCount: number;
  defaultIndex?: number;
  loop?: boolean;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  onChange?: (index: number) => void;
}

export interface UseCarouselReturn {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  next: () => void;
  previous: () => void;
  goTo: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

export function useCarousel({
  itemCount,
  defaultIndex = 0,
  loop = false,
  autoPlay = false,
  autoPlayInterval = 3000,
  onChange,
}: UseCarouselOptions): UseCarouselReturn {
  const [activeIndex, setActiveIndexState] = React.useState(defaultIndex);

  const setActiveIndex = React.useCallback(
    (index: number) => {
      setActiveIndexState(index);
      onChange?.(index);
    },
    [onChange]
  );

  const next = React.useCallback(() => {
    let newIndex = activeIndex + 1;
    if (newIndex >= itemCount) {
      newIndex = loop ? 0 : itemCount - 1;
    }
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex, itemCount, loop, setActiveIndex]);

  const previous = React.useCallback(() => {
    let newIndex = activeIndex - 1;
    if (newIndex < 0) {
      newIndex = loop ? itemCount - 1 : 0;
    }
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  }, [activeIndex, itemCount, loop, setActiveIndex]);

  const goTo = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < itemCount) {
        setActiveIndex(index);
      }
    },
    [itemCount, setActiveIndex]
  );

  // Auto play
  React.useEffect(() => {
    if (!autoPlay || itemCount <= 1) return;

    const timer = setInterval(() => {
      next();
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [autoPlay, autoPlayInterval, itemCount, next]);

  return {
    activeIndex,
    setActiveIndex,
    next,
    previous,
    goTo,
    isFirst: activeIndex === 0,
    isLast: activeIndex === itemCount - 1,
    canGoNext: loop || activeIndex < itemCount - 1,
    canGoPrevious: loop || activeIndex > 0,
  };
}
